import { addDayOffset, DateString } from '../../../../../common/src/date_string.ts';
import {
  CalendarSongListTypes,
  EventSongList,
  IntervalSongList,
  IntervalType,
  Org,
  OrgType,
  SongList,
  SongListEntry,
  SongListType,
} from '../../../../../common/src/model.ts';
import {
  hasInterval,
  normalizeHymnOfTheMonthSongs,
  SongListChanges,
  withoutBlankEntries,
} from '../../../../../common/src/model_util.ts';
import { ensureUnreachable } from '../../../../../common/src/util.ts';

// Pure row logic behind the planner's cells and its row inserts and deletes (issue #3609).
// A hymn-of-the-month list holds at most one song, and its row means nothing: readers take its single song
// whatever its row, and writers always store it at row 1.

/**
 * Returns `songList` with its entries shifted by `offset` from `rowIndex` down (a negative offset also removes the
 * entries at `rowIndex`), or `undefined` when row inserts and deletes do not apply to it. Hymn-of-the-month lists
 * never shift: a template row inserted above row 1 once moved them to row 3, where the planner could not see them.
 */
export function shiftSongListRows(
  songList: SongList,
  { rowIndex, offset, intervalTypes }: { rowIndex: number; offset: number; intervalTypes?: IntervalType[] },
): SongList | undefined {
  if (
    songList.type === SongListType.HymnOfTheMonth ||
    CalendarSongListTypes.includes(songList.type) ||
    (intervalTypes && hasInterval(songList) && !intervalTypes.includes(songList.interval.type))
  ) {
    return undefined;
  }
  return {
    ...songList,
    songs: songList.songs
      .filter(({ row }) => offset > 0 || row !== rowIndex) // remove rows if offset negative
      .map((song) => ({ ...song, row: song.row >= rowIndex ? song.row + offset : song.row })),
  };
}

/**
 * The entry a planner cell at `row` shows: for a hymn-of-the-month list, its single song at any row (the row 1
 * entry, else the lowest-row one). Returns the stored entry itself, never a copy, so a cell's memoized callbacks
 * stay stable across renders; its row may not be 1, and writes go through replaceSongListEntry, which normalizes.
 */
export function findSongListEntry(songList: SongList, row: number): SongListEntry | undefined {
  if (songList.type === SongListType.HymnOfTheMonth) {
    const { songs } = songList;
    return (
      songs.find((song) => song.row === 1) ??
      songs.reduce<SongListEntry | undefined>(
        (lowest, song) => (lowest && lowest.row <= song.row ? lowest : song),
        undefined,
      )
    );
  }
  return songList.songs.find((song) => song.row === row);
}

/** `songList` with the cell at `row` set to `entry`: a hymn-of-the-month list becomes that one entry, at row 1. */
export function replaceSongListEntry<T extends SongList>(songList: T, row: number, entry: SongListEntry): T {
  if (songList.type === SongListType.HymnOfTheMonth) {
    return { ...songList, songs: normalizeHymnOfTheMonthSongs([entry]) };
  }
  return {
    ...songList,
    songs: [...songList.songs.filter((song) => song.row !== row), entry].sort((lhs, rhs) => lhs.row - rhs.row),
  };
}

/** `songList` with the cell at `row` cleared: a hymn-of-the-month list loses its song whatever its row. */
export function removeSongListEntry<T extends SongList>(songList: T, row: number): T {
  if (songList.type === SongListType.HymnOfTheMonth) {
    return { ...songList, songs: [] };
  }
  return { ...songList, songs: songList.songs.filter((song) => song.row !== row) };
}

/** An unsaved, empty weekly list for `date`: a worship service for a church or small group, a Week interval for a school. */
export function getEmptyWeeklySongList(org: Org | undefined, date: DateString): EventSongList | IntervalSongList {
  const type = org?.type ?? OrgType.Church;
  switch (type) {
    case OrgType.Church:
    case OrgType.SmallGroup:
      return { type: SongListType.WorshipService, date: date, songs: [] };
    case OrgType.School:
      return {
        type: SongListType.Interval,
        interval: { type: IntervalType.Week, start: date, end: addDayOffset(date, 6) },
        songs: [],
      };
    default:
      ensureUnreachable(type);
  }
}

/**
 * Splits the lists a write is about to save into the ones to write and the ones to delete, so that a church's weekly
 * list (its worship service) never holds a blank in the past and is never empty (issue #4190). Michael: "the past
 * should never have explicitly blank cells but only empty cells. Also, the past should never have an empty song
 * list, nor should the future." And: "Blanking all cells in a column removes the song list entirely."
 * - A church Sunday dated before `currentSunday` loses its blanks (a blank only interrupts a future repeat). A
 *   planner edit of a passed Sunday the server has not written down yet writes it down first, in the same write
 *   (issue #4209, planner_edits.ts), which moves its blanks on to the next Sunday in scope, so none is lost here.
 * - A church Sunday then left with no entry at all is deleted instead of written, so it reads as a Sunday that never
 *   had a list. One never saved (no id) has nothing to delete and is dropped. One with a temporary negative id is
 *   deleted like any other: the update queue keeps only its latest payload, the delete, and the sync sends that
 *   delete under the server id the list was uploaded as, or drops it when the list was never uploaded.
 * - A church Sunday on or after `currentSunday` holding only blanks is not empty and is written as given.
 * - Every other list, including every school and small group list and every event, is written as given.
 * Lists to write keep the given order. The date rule lives here, on the admin's device, and not on the server: the
 * current Sunday is the admin's, and the server's clock is not in the admin's time zone (a Sunday-evening write in
 * the US is Monday in UTC). A blank on a Sunday that has since passed reads as an empty cell everywhere.
 */
export function normalizeChurchWeeklyListWrites(
  org: Pick<Org, 'type'> | undefined,
  currentSunday: DateString,
  songLists: SongList[],
): SongListChanges {
  const changes: SongListChanges = { update: [], remove: [] };
  for (const songList of songLists) {
    if (org?.type !== OrgType.Church || songList.type !== SongListType.WorshipService) {
      changes.update.push(songList);
      continue;
    }
    const songs = songList.date < currentSunday ? withoutBlankEntries(songList.songs) : songList.songs;
    if (songs.length > 0) {
      changes.update.push(songs === songList.songs ? songList : { ...songList, songs });
    } else if (songList.id !== undefined) {
      changes.remove.push(songList);
    }
  }
  return changes;
}
